import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  type FirestoreSettings,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  type AppCheck,
} from 'firebase/app-check';
import { z } from 'zod';

// Validate at boot so a missing VITE_* var fails loudly on startup instead
// of surfacing as an opaque Firebase runtime error later.
const EnvSchema = z.object({
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),
  // Optional — if set, App Check is initialized at boot. If absent, the
  // app still works but can't pass App Check-enforced backends. Set this
  // once you've registered a reCAPTCHA v3 site in Firebase Console.
  VITE_FIREBASE_APPCHECK_SITE_KEY: z.string().optional(),
});

const parsed = EnvSchema.safeParse(import.meta.env);
if (!parsed.success) {
  const missing = parsed.error.issues.map((i) => i.path.join('.')).join(', ');
  throw new Error(
    `Missing or invalid Firebase env vars: ${missing}. ` +
      `Copy .env.example to .env.local and fill in the VITE_FIREBASE_* keys.`,
  );
}

const env = parsed.data;

const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

// Firebase App Check — attests that requests come from *our* app, not a
// curl script or stolen ID token replayed from elsewhere. Opt-in: only
// initializes when the reCAPTCHA v3 site key is set. Enforcement is
// toggled per-service in the Firebase Console → App Check tab.
//
// In dev, enable the built-in debug provider so we don't have to solve
// a captcha on every hot-reload. This is a development-only affordance:
// the debug token is printed to the console on first run and must be
// registered in Firebase Console → App Check → <app> → Manage debug tokens.
// The live App Check instance, or null when the site key is absent (dev
// without a key, and the E2E harness). `appCheckRecovery` needs the
// handle to force a token refresh when a write comes back
// permission-denied — the auto-refresh above is a timer, and a phone
// that just woke up can't wait for the next tick.
let appCheckInstance: AppCheck | null = null;

if (env.VITE_FIREBASE_APPCHECK_SITE_KEY) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
  try {
    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(env.VITE_FIREBASE_APPCHECK_SITE_KEY),
      // Auto-refresh keeps long-lived sessions from getting 401'd by an
      // expired App Check token. Small cost in background traffic, big
      // UX win for warehouse staff who leave tabs open all day.
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Don't hard-fail boot if App Check init throws (e.g. misconfigured
    // site key). The user can still sign in; they'll get clear
    // permission-denied errors once the backend enforces App Check,
    // which is a better failure mode than a blank page.
     
    console.warn('[firebase] App Check initialization failed:', err);
  }
}

export const appCheck: AppCheck | null = appCheckInstance;

export const auth = getAuth(app);

const useEmulators = import.meta.env.VITE_USE_EMULATORS === '1';

// IndexedDB-backed offline cache. Snapshot listeners (the whole barrels
// collection, the 1500-row tasting collection-group) serve from disk
// instantly on repeat loads and keep working through flaky warehouse
// Wi-Fi, instead of re-pulling over the network on every navigation.
// `persistentMultipleTabManager` shares one cache across tabs without
// the old `enableIndexedDbPersistence` multi-tab throw. Must run before
// any other Firestore access — this module is the single `db` source.
const firestoreSettings: FirestoreSettings = {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
  // E2E-only: force the long-polling transport against the emulator.
  // WebKit's streaming WebChannel handshake is unreliable against the
  // Firestore emulator — the HTTP POSTs 200 but the Listen-stream snapshot
  // delivery stalls, so a single-doc `getDoc()` (e.g. the parent-barrel
  // read inside logTasting / bottleLiquid) hangs and the save never
  // completes. This surfaced as stuck bottling/tasting saves on
  // webkit-iphone. Long-polling is deterministic where the stream isn't.
  // Prod/dev keep Firestore's auto-detected transport (real iPhone Safari
  // talks to real Firestore over WebChannel just fine), so this is dead
  // config everywhere except the emulator harness.
  ...(useEmulators ? { experimentalForceLongPolling: true } : {}),
};
export const db = initializeFirestore(app, firestoreSettings);

// E2E-only: point Auth + Firestore at local emulators. Opt-in via
// VITE_USE_EMULATORS=1 (set by .env.e2e / the Playwright webServer) —
// the flag is absent in dev and prod builds, so this block is dead
// code everywhere except the E2E harness. Must run before any
// Firestore/Auth traffic, which is why it lives here right after init.
// App Check is naturally skipped in E2E: .env.e2e sets no site key.
if (useEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  // Route callables (undoVolumeDraw, allocateBlendNumber, …) to the local
  // functions emulator — the server declares no region, so the default
  // us-central1 instance both client Functions handles resolve to is covered.
  connectFunctionsEmulator(getFunctions(app), '127.0.0.1', 5001);
}

export const storage = getStorage(app);
export default app;
